import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { useAutoTranslation, useUserStore } from "@/entities"
import { RegisterForm } from "@/features"
import {
  authControllerCancelPendingGuestUpgrade,
  authControllerConfirmPendingGuestUpgrade,
  authControllerGetPendingGuestUpgrade,
  authControllerResendPendingGuestUpgrade,
} from "@/shared/api/generated"
import { DISCOVERED_TRANSLATION_KEYS, ROUTER_PATHS } from "@/shared/constants"
import { notifyExtension } from "@/shared/utils/notify-extension"
import { AxiosError } from "axios"
import { useEffect, useState } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import { toast } from "sonner"

export const ConnectEmailPage = () => {
  const isGuest = useUserStore(state => state.isGuest)
  const login = useUserStore(state => state.login)
  const { t } = useAutoTranslation()
  const navigate = useNavigate()
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [code, setCode] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const loadPending = async () => {
    const pending = await authControllerGetPendingGuestUpgrade()
    setPendingEmail(pending.pendingEmail)
    setEditing(false)
  }

  useEffect(() => {
    if (isGuest) void loadPending()
  }, [isGuest])

  if (!isGuest) return <Navigate to={ROUTER_PATHS.PROFILE_CHANGE_VOICE} replace />

  const confirm = async () => {
    setLoading(true)
    setError("")
    try {
      const result = await authControllerConfirmPendingGuestUpgrade({ code })
      await login(result.access_token)
      await notifyExtension(
        new URLSearchParams(window.location.search).get("extensionId"),
        "EXTENSION_ACCOUNT_UPDATED"
      )
      navigate(ROUTER_PATHS.APP_LIBRARY, { replace: true })
    } catch (cause) {
      setError(cause instanceof AxiosError ? String(cause.response?.data?.message ?? "Invalid confirmation code") : "Invalid confirmation code")
    } finally {
      setLoading(false)
    }
  }

  const cancel = async () => {
    setLoading(true)
    try {
      await authControllerCancelPendingGuestUpgrade()
      setPendingEmail(null)
      setEditing(false)
      setCode("")
      navigate(ROUTER_PATHS.APP_LIBRARY)
    } finally {
      setLoading(false)
    }
  }

  const resend = async () => {
    setLoading(true)
    try {
      await authControllerResendPendingGuestUpgrade()
      toast.success(t(DISCOVERED_TRANSLATION_KEYS.CONFIRMATION_CODE_SENT))
    } catch (cause) {
      toast.error(cause instanceof AxiosError ? String(cause.response?.data?.message ?? "Could not resend code") : "Could not resend code")
    } finally {
      setLoading(false)
    }
  }

  const showVerification = !!pendingEmail && !editing

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{showVerification ? t(DISCOVERED_TRANSLATION_KEYS.CONFIRM_YOUR_EMAIL) : t(DISCOVERED_TRANSLATION_KEYS.CONNECT_EMAIL)}</CardTitle>
          <CardDescription>
            {showVerification
              ? `${t(DISCOVERED_TRANSLATION_KEYS.GUEST_PENDING_EMAIL_DESCRIPTION)} ${pendingEmail}`
              : t(DISCOVERED_TRANSLATION_KEYS.CONNECT_EMAIL_DESCRIPTION)}
          </CardDescription>
        </CardHeader>
        {showVerification ? (
          <>
            <CardContent className="space-y-3">
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                placeholder="000000"
                className="text-center text-xl tracking-[0.45em]"
                onChange={event => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
            </CardContent>
            <CardFooter className="flex flex-wrap gap-2">
              <Button disabled={code.length !== 6} isLoading={loading} onClick={confirm}>{t(DISCOVERED_TRANSLATION_KEYS.CONFIRM_EMAIL)}</Button>
              <Button variant="outline" disabled={loading} onClick={() => setEditing(true)}>{t(DISCOVERED_TRANSLATION_KEYS.EDIT_EMAIL)}</Button>
              <Button variant="ghost" disabled={loading} onClick={resend}>{t(DISCOVERED_TRANSLATION_KEYS.RESEND_CODE)}</Button>
              <Button variant="ghost" disabled={loading} onClick={cancel}>{t(DISCOVERED_TRANSLATION_KEYS.CANCEL)}</Button>
            </CardFooter>
          </>
        ) : (
          <CardContent>
            <RegisterForm onGuestUpgradeStarted={loadPending} />
            {pendingEmail && <Button className="mt-3 w-full" variant="ghost" onClick={() => setEditing(false)}>{t(DISCOVERED_TRANSLATION_KEYS.BACK_TO_CONFIRMATION)}</Button>}
          </CardContent>
        )}
      </Card>
    </div>
  )
}
